import type { ReactNode } from "react"
import { memo, useCallback, useMemo, useRef, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { Link, useNavigate } from "@tanstack/react-router"
import { useCommandState } from "cmdk"
import { IconUserFill18 } from "nucleo-ui-fill-18"
import {
	IconCheckOutline18 as Check,
	IconChevronRightOutline18 as ChevronRight,
	IconBanOutline18,
	IconUserXmarkOutline18 as UserX,
} from "nucleo-ui-outline-18"
import { useToggle } from "react-use"
import { FixedSizeList } from "react-window"

import type { BrandfetchData } from "@productlane/api/src/browser"
import type { PopoverProps } from "@productlane/ui"
import { generateNameFromEmail } from "@productlane/api/src/lib/name-from-email"
import { cn } from "@productlane/lib/src/browser"
import { ShortcutSymbols } from "@productlane/lib/src/shortcut-symbols"
import {
	Avatar,
	Button,
	CopyButton,
	IconButton,
	Popover,
	Shortcut,
} from "@productlane/ui"
import {
	Command,
	CommandAddNewItem,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator,
} from "@productlane/ui/src/components/internal/command"
import { queries } from "@productlane/zero/queries"

import type { PartialCustomerCreate } from "../customers/create-customer-dialog"
import type { PartialContactCreate } from "./create-contact-dialog"
import { dedupeById } from "@/components/filters"
import { toast } from "@/components/toast"
import { useShortcut } from "@/lib"
import { analytics } from "@/lib/analytics"
import { useContactOptions } from "@/lib/use-entity-options"
import { useRole } from "@/lib/useRole"
import { CompanyLogo } from "../customers/company-logo"
import { filterCompanies } from "../customers/filter-companies"
import { selectorActionClassName } from "../selector-action-button"
import { CreateContactDialog } from "./create-contact-dialog"
import {
	MAX_CONTACT_SUGGESTIONS,
	mergeContactSuggestions,
	pickCompanyContacts,
	pickSuggestedContacts,
	rankCompanyContactIds,
} from "./thread-contact-suggestions"
import { UpdateContactDialog } from "./update-contact-dialog"

const NO_SUGGESTIONS: readonly string[] = []

// The fields the trigger renders for whoever is currently selected.
interface SelectedContact {
	id: string
	email: string
	name?: string | null
	imageUrl?: string | null
}

interface ContactSelectProps {
	contactId: string | null
	// A caller that already holds the row passes it here. Every thread query
	// relates its contact, so passing it saves a synced query per thread open.
	contact?: SelectedContact | null
	onChange: (contact: PartialContactCreate | null) => void
	className?: string
	align?: PopoverProps["align"]
	modal?: PopoverProps["modal"]
	speakerName?: string
	withoutIcon?: boolean
	displayEmail?: boolean
	displayAsPrimary?: boolean
	isBlocked?: boolean
	trigger?: ReactNode
	showCompanies?: boolean
	// Also seeds the suggestions with this company's most active people.
	companyId?: string | null
	onCompanySelect?: (company: PartialCustomerCreate) => void
	// Normalized emails, best first; matching contacts show above the full list.
	suggestedEmails?: readonly string[]
	// Same, but ranked below the company's people.
	suggestedTeamEmails?: readonly string[]
}

export const ContactSelect = memo(
	({
		onChange,
		contactId,
		contact,
		className,
		align,
		modal,
		speakerName,
		withoutIcon,
		displayEmail,
		displayAsPrimary,
		isBlocked,
		trigger,
		showCompanies = false,
		companyId,
		onCompanySelect,
		suggestedEmails = NO_SUGGESTIONS,
		suggestedTeamEmails = NO_SUGGESTIONS,
	}: ContactSelectProps) => {
		const { role } = useRole()
		const [open, onOpenChange] = useToggle(false)

		// A thread that already has a person takes its customer from that person,
		// so the customer list only helps while no person is picked.
		const listCompanies = showCompanies && !contactId

		const [query, setQuery] = useState("")
		// The company list covers the whole workspace, so it stays unsynced until
		// the picker opens; the selected person comes from its own row instead.
		const { contacts: contactOptions } = useContactOptions({
			query,
			enabled: open,
		})
		const suggestionEmails = useMemo(
			() => [...suggestedEmails, ...suggestedTeamEmails].slice(0, 200),
			[suggestedEmails, suggestedTeamEmails],
		)
		const [emailContacts] = useQuery(
			queries.contactsByEmails(suggestionEmails),
			{ enabled: open && suggestionEmails.length > 0 },
		)
		const [companyContacts] = useQuery(
			queries.contactsByCompany({
				companyId: companyId ?? "",
				limit: MAX_CONTACT_SUGGESTIONS + 1,
			}),
			{ enabled: open && !!companyId },
		)
		const [companies] = useQuery(queries.companiesWithMeta(), {
			enabled: open && listCompanies,
		})
		const [companyThreads] = useQuery(
			queries.companyContactThreadSample(companyId ?? ""),
			{ enabled: open && !!companyId },
		)
		const companyContactIds = useMemo(
			() => rankCompanyContactIds(companyThreads),
			[companyThreads],
		)
		const [activeContacts] = useQuery(
			queries.contactsByIds(companyContactIds),
			{ enabled: open && companyContactIds.length > 0 },
		)
		const contacts = useMemo(
			() =>
				dedupeById([
					...contactOptions,
					...emailContacts,
					...companyContacts,
					...activeContacts,
				]),
			[contactOptions, emailContacts, companyContacts, activeContacts],
		)
		const [queriedContact] = useQuery(queries.contactById(contactId ?? ""), {
			enabled: !!contactId && contact === undefined,
		})
		const selectedContact: SelectedContact | undefined =
			contact === undefined ? queriedContact : (contact ?? undefined)

		const [contactCreateValue, setContactCreateValue] = useState("")
		const filteredContacts = useMemo(() => {
			const lowerQuery = query.toLowerCase()
			return contacts.filter(
				(c) =>
					c.name?.toLowerCase().includes(lowerQuery) ||
					c.email.toLowerCase().includes(lowerQuery),
			)
		}, [contacts, query])
		const suggestedContacts = useMemo(
			() =>
				query
					? []
					: mergeContactSuggestions(
							pickSuggestedContacts(contacts, suggestedEmails, contactId),
							pickCompanyContacts(contacts, {
								companyId,
								rankedContactIds: companyContactIds,
								excludedContactIds: [contactId],
							}),
							pickSuggestedContacts(contacts, suggestedTeamEmails, contactId),
						),
			[
				contacts,
				suggestedEmails,
				suggestedTeamEmails,
				contactId,
				companyId,
				companyContactIds,
				query,
			],
		)
		const listedContacts = useMemo(() => {
			if (suggestedContacts.length === 0) return filteredContacts
			const suggestedIds = new Set(suggestedContacts.map((c) => c.id))
			return filteredContacts.filter((c) => !suggestedIds.has(c.id))
		}, [filteredContacts, suggestedContacts])
		const filteredCompanies = useMemo(() => {
			if (!listCompanies) return []
			return filterCompanies(companies, query)
		}, [companies, query, listCompanies])
		const contactsListHeight = listCompanies
			? Math.min(listedContacts.length * 36, 180)
			: 256
		const companiesListHeight = Math.min(filteredCompanies.length * 36, 180)
		const parentRef = useRef<HTMLDivElement>(null)
		const navigate = useNavigate()

		useShortcut(
			"meta+shift+u, ctrl+shift+u",
			() => {
				onOpenChange(true)
			},
			{
				enableOnContentEditable: true,
				preventDefault: true,
				enabled: role !== "VIEWER",
			},
		)

		const [isUpdateContactOpen, setIsUpdateContactOpen] = useToggle(false)

		const UpdateFormDialog = useCallback(() => {
			return (
				<>
					{selectedContact && (
						<UpdateContactDialog
							isOpen={isUpdateContactOpen}
							onOpenChange={setIsUpdateContactOpen}
							values={{
								id: selectedContact?.id,
								email: selectedContact?.email,
								name: selectedContact?.name ?? "",
							}}
						/>
					)}
				</>
			)
		}, [selectedContact, isUpdateContactOpen, setIsUpdateContactOpen])

		const contactName = selectedContact?.name
			? selectedContact.name
			: selectedContact?.email
				? selectedContact.email
				: ""

		return (
			<>
				<UpdateFormDialog />
				<CreateContactDialog
					defaultValue={contactCreateValue}
					isOpen={!!contactCreateValue}
					onOpenChange={(open, newContact) => {
						if (!open) {
							if (newContact) {
								onChange(newContact)
								onOpenChange(false)
								setQuery("")
								analytics.track("created-inline-contact")
							}
							setContactCreateValue("")
							analytics.track("close-inline-create-contact-dialog")
						}
					}}
				/>
				<div
					className={cn(
						"group relative flex min-w-52 items-center gap-0.5 truncate",
						className,
					)}
				>
					<Popover
						open={open}
						onOpenChange={onOpenChange}
						modal={modal}
						className="p-0"
						side="left"
						align={align}
						triggerTooltip={
							<div className="flex gap-1">
								Select contact{" "}
								<Shortcut className="ml-1">{ShortcutSymbols.CMD}</Shortcut>
								<Shortcut>{ShortcutSymbols.SHIFT}</Shortcut>
								<Shortcut>{ShortcutSymbols.U}</Shortcut>
							</div>
						}
						content={
							<Command shouldFilter={false}>
								<div className="relative">
									<CommandInput
										autoFocus
										placeholder={
											listCompanies
												? "Search people or customers..."
												: "Search people..."
										}
										value={query}
										onValueChange={setQuery}
										className={cn("max-w-[9.5rem] md:max-w-[11.5rem]", {
											"max-w-[9.5rem] md:max-w-[11.5rem]": query,
										})}
									/>
									<div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1">
										<div className="flex items-center gap-1.5">
											<Shortcut>⌘</Shortcut>
											<Shortcut>⇧</Shortcut>
											<Shortcut>U</Shortcut>
										</div>
									</div>
								</div>
								<CommandList ref={parentRef} className="max-h-none">
									{suggestedContacts.length > 0 && (
										<>
											<CommandGroup heading="Suggested">
												{suggestedContacts.map((contact) => (
													<CommandItem
														key={contact.id}
														value={`suggested-${contact.id}`}
														onSelect={() => {
															onChange(contact)
															onOpenChange(false)
															setQuery("")
															analytics.track("selected-suggested-contact")
														}}
													>
														<div className="flex min-w-0 items-center gap-2">
															<Avatar
																size="xs"
																name={contact.name || contact.email}
																colorSeed={contact.email}
																image={contact.imageUrl}
															/>
															<span className="truncate">
																{contact.name || contact.email}
															</span>
															{contact.name && (
																<span className="min-w-0 flex-1 truncate text-tertiary">
																	{contact.email}
																</span>
															)}
														</div>
													</CommandItem>
												))}
											</CommandGroup>
											<CommandSeparator />
										</>
									)}
									{filteredContacts.length > 0 && (
										<CommandGroup>
											{selectedContact?.id && (
												<CommandItem
													onSelect={() => {
														onChange(null)
														onOpenChange(false)
													}}
												>
													<div className="flex items-center gap-2 px-1">
														<UserX className="h-4 w-4" />
														<span className="flex-1 truncate">No person</span>
													</div>
												</CommandItem>
											)}
											<div style={{ height: contactsListHeight }}>
												<FixedSizeList
													height={contactsListHeight}
													width="100%"
													itemCount={listedContacts.length}
													itemData={listedContacts}
													itemKey={(index, data) => data[index]?.id ?? index}
													itemSize={36}
													overscanCount={5}
													className="!h-full"
												>
													{({ index, style }) => {
														const contact = listedContacts[index]

														if (!contact) return null

														return (
															<CommandItem
																value={contact.id}
																onSelect={() => {
																	onChange(contact)
																	onOpenChange(false)
																	setQuery("")
																}}
																style={style}
																key={contact.id}
															>
																<div className="flex items-center gap-2">
																	<Avatar
																		size="xs"
																		name={contact.name || contact.email}
																		colorSeed={contact.email}
																		image={contact.imageUrl}
																	/>
																	<span className="flex-1 truncate">
																		{contact.name
																			? contact.name
																			: contact.email}
																	</span>
																	{contactId === contact.id && (
																		<Check className="h-4 w-4" />
																	)}
																</div>
															</CommandItem>
														)
													}}
												</FixedSizeList>
											</div>
										</CommandGroup>
									)}
									<AddNewItem
										show={
											filteredContacts.length === 0 &&
											filteredCompanies.length === 0
										}
										onCreate={(value) => {
											setContactCreateValue(value)
										}}
									/>

									{listCompanies && filteredCompanies.length > 0 && (
										<>
											<CommandSeparator />
											<CommandGroup heading="Customers">
												<FixedSizeList
													height={companiesListHeight}
													width="100%"
													itemCount={filteredCompanies.length}
													itemData={filteredCompanies}
													itemKey={(index, data) => data[index]?.id ?? index}
													itemSize={36}
													overscanCount={5}
												>
													{({ index, style }) => {
														const company = filteredCompanies[index]

														if (!company) return null

														return (
															<CommandItem
																value={`company-${company.id}`}
																onSelect={() => {
																	onCompanySelect?.(company)
																	onOpenChange(false)
																	setQuery("")
																}}
																style={style}
																key={company.id}
															>
																<div className="flex items-center gap-2">
																	<CompanyLogo
																		brandfetchData={
																			company.companyMeta
																				?.brandfetchData as BrandfetchData
																		}
																		fallback={company.name}
																		size="xs"
																		linearLogoUrl={company.logoUrl || undefined}
																	/>
																	<span className="flex-1 truncate">
																		{company.name}
																	</span>
																	{companyId === company.id && (
																		<Check className="h-4 w-4" />
																	)}
																</div>
															</CommandItem>
														)
													}}
												</FixedSizeList>
											</CommandGroup>
										</>
									)}
								</CommandList>
							</Command>
						}
					>
						{trigger ? (
							trigger
						) : speakerName ? (
							<div className="flex cursor-default items-center gap-2 rounded-md px-2 py-1 hover:bg-gray-50 hover:dark:bg-gray-850">
								<Avatar
									roundness="full"
									size="xs"
									image={selectedContact?.imageUrl}
									name={selectedContact?.name ?? speakerName}
									colorSeed={selectedContact?.email ?? speakerName}
								/>
								<div className="cursor-default text-lg font-semibold text-primary">
									{selectedContact?.name ?? speakerName}
								</div>
							</div>
						) : (
							<Button
								variant="ghost"
								className={cn(
									"h-auto flex-1 justify-start truncate text-xs hover:bg-gray-200 group-hover:bg-gray-200 dark:hover:bg-gray-800 dark:group-hover:bg-gray-800",
									role === "VIEWER" && "pointer-events-none",
								)}
								// The 14px glyph sits in the 16px slot the selected avatar takes, so the label does not jump on pick.
								iconClassName="mx-px !text-tertiary"
								{...(withoutIcon
									? {}
									: {
											Icon:
												contactId && selectedContact
													? undefined
													: IconUserFill18,
										})}
							>
								{contactId && selectedContact ? (
									<>
										{!withoutIcon &&
											(displayAsPrimary ? (
												<Link
													to="/people/$id"
													params={{ id: selectedContact.id }}
													onClick={(e) => e.stopPropagation()}
													className="mr-2 shrink-0"
												>
													<Avatar
														name={contactName}
														colorSeed={selectedContact.email}
														image={selectedContact.imageUrl}
														size="xxs"
													/>
												</Link>
											) : isBlocked ? (
												<IconBanOutline18 className="mr-2 size-4 shrink-0" />
											) : (
												<Avatar
													name={contactName}
													colorSeed={selectedContact.email}
													image={selectedContact.imageUrl}
													size="xxs"
													className="mr-2"
												/>
											))}

										<span
											className={cn(
												"truncate text-xs font-normal text-primary",
											)}
										>
											{displayAsPrimary
												? selectedContact.email
												: displayEmail
													? selectedContact.name ||
														generateNameFromEmail(selectedContact.email)
													: selectedContact.name
														? selectedContact.name
														: selectedContact.email}
										</span>
									</>
								) : (
									<span
										className={cn(
											"truncate",
											displayEmail
												? "text-xs text-tertiary"
												: "text-lg font-semibold text-primary",
										)}
									>
										Select person
									</span>
								)}
							</Button>
						)}
					</Popover>

					{selectedContact && !speakerName && !displayAsPrimary ? (
						<div className="flex items-center gap-1 opacity-0 group-hover/contact:opacity-100 group-hover:opacity-100">
							<CopyButton
								value={selectedContact.email}
								tooltip="Copy contact email"
								className={cn("size-6 rounded-full", selectorActionClassName)}
								iconClassName="size-3.5"
								onCopied={() => toast.success({ title: "Email copied" })}
							/>

							<IconButton
								Icon={ChevronRight}
								variant="ghost"
								size="md"
								className={cn("px-1.5", selectorActionClassName)}
								onClick={() => {
									void navigate({
										to: "/people/$id",
										params: { id: selectedContact.id },
									})
								}}
							/>
						</div>
					) : null}
				</div>
			</>
		)
	},
)

interface AddNewItemProps {
	onCreate: (value: string) => void
	show: boolean
}

const AddNewItem = memo(({ onCreate, show }: AddNewItemProps) => {
	const search = useCommandState((state) => state.search).trim()

	return (
		<CommandAddNewItem
			onCreate={onCreate}
			show={show}
			search={search}
			onAnalyticsTrack={() => {
				analytics.track("open-inline-create-contact-dialog")
			}}
		/>
	)
})
